'use client';

import React, { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useUser } from '@/firebase';
import AppShell from '@/components/layout/app-shell';

const NO_SHELL_PREFIXES = ['/login', '/web'];

export default function AppShellGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const { user, loading } = useUser();

  const isPublic =
    pathname === '/' || NO_SHELL_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    // This guard is only for protected app routes
    if (isPublic) return;

    const hasCookie = typeof document !== 'undefined' && document.cookie.includes('ti_session=');

    // If the check is done, there's no user, but a session cookie exists,
    // it means the session is invalid. Clean up and redirect to login.
    if (!loading && !user && hasCookie) {
      // Clear the invalid cookie
      document.cookie = 'ti_session=; Max-Age=0; Path=/; SameSite=Lax; Secure;';
      // Redirect to login, preserving the intended destination
      router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
    }
  }, [user, loading, router, pathname, isPublic]);

  if (isPublic) {
    return <>{children}</>;
  }

  // For protected routes, show a loading state while auth is being checked
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground">Verifying session...</p>
      </div>
    );
  }

  // If there's no user and no cookie, the middleware should have already redirected.
  // But if we get here, render nothing while redirect occurs.
  if (!user) {
    return null;
  }
  
  // If we have a user, render the main app shell.
  return <AppShell>{children}</AppShell>;
}
